import { ResizeComponent, type SizeProps } from "containers/hooks/ResizeComponent";
import * as d3 from "d3";
import { useEffect, useRef } from "react";

export type PieChartData = { key: string; value: number; color: string };

type ComponentProps = {
  id?: string;
  data: PieChartData[];
  totalCount: number;
};

const margin = { top: 10, right: 10, bottom: 10, left: 10 };

const AnimatedPieChart = (props: ComponentProps & SizeProps) => {
  const d3Container = useRef<SVGGElement>(null);
  const chartLabels = useRef<SVGGElement>(null);

  /** Stores the current piechart data states for animating the parts **/
  const animationRef = useRef<d3.PieArcDatum<PieChartData>[]>([]);
  const { centerX, centerY, radius } = getChartCircleSettings();

  /**
   * Returns the position of the pie chart (centered in the middle of the drawing area)
   */
  function getChartCircleSettings() {
    const chartWidth = props.size.width - margin.left - margin.right;
    const chartHeight = props.size.height - margin.top - margin.bottom;
    const circleRadius = Math.floor(Math.min(chartWidth, chartHeight) / 2);

    return {
      centerX: margin.left + Math.floor(chartWidth / 2),
      centerY: margin.top + Math.floor(chartHeight / 2),
      radius: circleRadius,
    };
  }

  /**
   * This useEffect block is run every time the data or component sizes change. We will do every chart updates here.
   */
  useEffect(() => {
    // D3 function that extends the data elements with geometry for the pie slice
    const pie = d3.pie<PieChartData>().value((d) => d.value);

    const pieElementsData = pie(props.data);
    if (d3Container.current) {
      const svg = d3.select(d3Container.current);

      // Set offset to position the chart in the center of the svg area
      const svgChart = svg.attr("transform", `translate(${centerX}, ${centerY})`);

      // Mouse Events on pie slices
      // const mouseOver = function (this: SVGPathElement) {
      //   d3.select(this)
      //     .style("stroke-width", "3px");
      // }

      // const mouseLeave = function (this: SVGPathElement) {
      //   d3.select(this)
      //     .style("stroke-width", "1px");
      // }

      // const mouseDown = function (_event: MouseEvent, d: d3.PieArcDatum<PieChartData>) {
      //   console.log(d.data);
      // }

      // D3 function that maps the slices into SVG <path>s
      // Increase innerRadius for a donut chart
      const arcFunc = (d: d3.PieArcDatum<PieChartData>) =>
        d3.arc()({
          innerRadius: 0,
          outerRadius: radius,
          startAngle: d.startAngle,
          endAngle: d.endAngle,
        });

      const keyFunc = (d: d3.PieArcDatum<PieChartData>) => d.data.key;
      const pieElements = svgChart
        .selectAll<SVGPathElement, d3.PieArcDatum<PieChartData>>("path")
        .data(pieElementsData, keyFunc);

      // Enter new elements
      pieElements
        .enter()
        .append("path")
        .attr("d", (d) => arcFunc(d))
        .style("fill", (d) => d.data.color)
        .style("stroke", "black")
        .style("stroke-width", "1px")
        // .on("mousedown", mouseDown)
        // .on("mouseover", mouseOver)
        // .on("mouseleave", mouseLeave)
        .each((d, i) => {
          // Store current state in the animationRef
          animationRef.current[i] = d;
        });

      // Update existing elements
      pieElements
        .transition()
        .duration(500)
        .attrTween("d", (d: d3.PieArcDatum<PieChartData>, i) => {
          // Use the stored state in the animationRef for the interpolation
          const interpolate = d3.interpolate(animationRef.current[i], d);

          return (t: number) => {
            const current = interpolate(t);
            animationRef.current[i] = current;
            return arcFunc(current) || "";
          };
        })
        .style("fill", (d) => d.data.color);

      // Remove old elements
      pieElements
        .exit()
        .each((_, i) => animationRef.current.splice(i, 1))
        .remove();
    }

    // draw labels
    if (chartLabels.current) {
      // update labels
      const arcLabels = d3.arc<d3.PieArcDatum<PieChartData>>().innerRadius(radius).outerRadius(radius);
      const arc = d3.arc<d3.PieArcDatum<PieChartData>>().innerRadius(0).outerRadius(radius);

      const nodeLabels = d3.select(chartLabels.current);
      const midAngle = (d: d3.PieArcDatum<PieChartData>) => d.startAngle + (d.endAngle - d.startAngle) / 2;

      nodeLabels
        .selectAll("polyline")
        .data(pieElementsData)
        .join("polyline")
        .attr("fill", "none")
        .attr("stroke", "white")
        .attr("stroke-width", 2)
        .attr("stroke-opacity", 0.75)
        .attr("points", (d, index) => {
          const f = midAngle(d) < Math.PI ? 1 : -1;
          const posOuter = arcLabels.centroid(d);
          const posLabel = arcLabels.centroid(d);
          const posInner = arc.centroid(d).map((p, i) => p + (posLabel[i] - p) * 0.5);
          posLabel[0] = f * (radius * 1.1 + 64 * index);
          posLabel[0] += f * -20 + 12;

          if (
            index > 0 &&
            Math.abs(midAngle(pieElementsData[index]) - midAngle(pieElementsData[index - 1])) < Math.PI / 8
          ) {
            posOuter[1] += -1 * Math.sign(posOuter[1]) * 15;
            posLabel[1] += -1 * Math.sign(posLabel[1]) * 15;
          }

          return [posInner, posOuter, posLabel].join(", ");
        });

      nodeLabels
        .selectAll("text")
        .data(pieElementsData)
        .join("text")
        .attr("dy", "0.3em")
        .attr("fill", (d) => d.data.color)
        .attr("transform", (d: d3.PieArcDatum<PieChartData>, index) => {
          const f = midAngle(d) < Math.PI ? 1 : -1;
          const posLabel = arcLabels.centroid(d);
          posLabel[0] = f * (radius * 1.1 + 64 * index);
          posLabel[0] += f * 20 + 12;

          const deltaAngle =
            index > 0 ? midAngle(pieElementsData[index]) - midAngle(pieElementsData[index - 1]) : Math.PI;
          if (Math.abs(deltaAngle) < Math.PI / 8)
            posLabel[1] += -1 * Math.sign(deltaAngle) * Math.sign(posLabel[1]) * 15;

          return `translate(${posLabel})`;
        })
        .style("text-anchor", (d: d3.PieArcDatum<PieChartData>) => (midAngle(d) < Math.PI ? "start" : "end"))
        .text((d) => d3.format(".1%")(d.value / props.totalCount));

      nodeLabels
        .selectAll("path")
        .data(pieElementsData)
        .join("path")
        .attr("fill", (d) => d.data.color)
        .attr("transform", (d: d3.PieArcDatum<PieChartData>, index) => {
          const f = midAngle(d) < Math.PI ? 1 : -1;
          const posLabel = arcLabels.centroid(d);
          posLabel[0] = f * (radius + 64 * index);
          posLabel[0] += f * 15;
          posLabel[1] -= 12;

          const deltaAngle =
            index > 0 ? midAngle(pieElementsData[index]) - midAngle(pieElementsData[index - 1]) : Math.PI;
          if (Math.abs(deltaAngle) < Math.PI / 8)
            posLabel[1] += -1 * Math.sign(deltaAngle) * Math.sign(posLabel[1]) * 15;

          return `translate(${posLabel})`;
        });
      //.attr('d', d => settings.icons[d.data.name]);
    }
  }, [props.data.length, centerY, props.data, centerX, props.totalCount, radius]);

  return (
    <svg
      id={props.id}
      width={props.size.width}
      height={props.size.height}
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
    >
      <title>{`Pie Chart (${props.data.length} categories)`}</title>
      <g ref={d3Container} />
      <g ref={chartLabels} transform={`translate(${[props.size.width / 2, props.size.height / 4]})`} />
    </svg>
  );
};

export default ResizeComponent(AnimatedPieChart);
